import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ArrowRight, Save, Copy, RotateCcw, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const SUBSTRATES = ["Film (BOPP/PET/PE)", "Paper", "Foil", "Corrugated", "Folding carton", "Label stock", "Shrink sleeve", "Aluminum", "Rigid plastic"];

export default function InkCopilot() {
  const [presses, setPresses] = useState([]);
  const [defects, setDefects] = useState([]);
  const [form, setForm] = useState({
    customer_name: "",
    press_id: "",
    press_description: "",
    substrate: "",
    defect_slug: "",
    defect_description: "",
    pantone_color: "",
    anilox_bcm: "",
    anilox_lpi: "",
    additional_context: "",
  });
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState("");
  const [saving, setSaving] = useState(false);
  const outRef = useRef(null);

  useEffect(() => {
    api.get("/ink/presses").then(({ data }) => setPresses(data)).catch(() => {});
    api.get("/ink/defects").then(({ data }) => setDefects(data)).catch(() => {});
  }, []);

  const run = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!form.defect_slug && !form.defect_description.trim()) {
      toast.error("Pick a defect or describe what the customer is seeing"); return;
    }
    setRunning(true);
    setOutput("");
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("ruleai_token");
      const res = await fetch(`${backendUrl}/api/ink/copilot/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...form,
          anilox_bcm: form.anilox_bcm ? parseFloat(form.anilox_bcm) : null,
          anilox_lpi: form.anilox_lpi ? parseFloat(form.anilox_lpi) : null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setOutput(acc);
        if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
      }
    } catch (e) {
      toast.error("Analysis failed. Try again.");
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    if (!output.trim()) { toast.error("Generate an analysis first"); return; }
    setSaving(true);
    try {
      await api.post("/ink/reports", {
        customer_name: form.customer_name,
        context: form,
        report_markdown: output,
      });
      toast.success("Report saved to Ink → Saved Reports");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(output).then(() => toast.success("Report copied"));
  };

  const download = () => {
    const blob = new Blob([output], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const cust = (form.customer_name || "customer").replace(/\s+/g, "-").toLowerCase();
    a.href = url; a.download = `${cust}-analysis-${new Date().toISOString().slice(0,10)}.md`;
    a.click(); URL.revokeObjectURL(url);
  };

  const reset = () => {
    setForm({ customer_name: "", press_id: "", press_description: "", substrate: "", defect_slug: "", defect_description: "", pantone_color: "", anilox_bcm: "", anilox_lpi: "", additional_context: "" });
    setOutput("");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
          <Link to="/app/ink" className="hover:text-primary">Ink &amp; Print</Link> / Copilot
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="copilot-title">
          AI Technical Sales Copilot
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Feed it the customer&rsquo;s press, substrate, defect, and color. Get a 9-section technical analysis in ~30 seconds, ready to save or send.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* INPUT */}
        <div className="lg:col-span-2 border border-border bg-card p-6 rounded-sm h-fit lg:sticky lg:top-20" data-testid="copilot-input-panel">
          <div className="overline text-primary">◉ CASE INPUTS</div>

          <div className="mt-5 space-y-4">
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Customer *</label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                     className="rounded-sm" placeholder="Acme Packaging Co." data-testid="input-customer" />
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-1.5">Press (from database)</label>
              <Select value={form.press_id || "none"} onValueChange={(v) => setForm({ ...form, press_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="select-press"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None / describe below —</SelectItem>
                  {presses.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.press_type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-1.5">Press description (free text)</label>
              <Input value={form.press_description} onChange={(e) => setForm({ ...form, press_description: e.target.value })}
                     className="rounded-sm" placeholder="e.g. 8-color Mark Andy flexo, 20&quot; web" data-testid="input-press-desc" />
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-1.5">Substrate</label>
              <Select value={form.substrate || "none"} onValueChange={(v) => setForm({ ...form, substrate: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="select-substrate"><SelectValue placeholder="Choose one" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {SUBSTRATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-1.5">Defect (choose one)</label>
              <Select value={form.defect_slug || "none"} onValueChange={(v) => setForm({ ...form, defect_slug: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="select-defect"><SelectValue placeholder="e.g. Mottling" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Custom (describe below) —</SelectItem>
                  {defects.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-1.5">What the customer is seeing</label>
              <Textarea rows={3} value={form.defect_description}
                        onChange={(e) => setForm({ ...form, defect_description: e.target.value })}
                        className="rounded-sm resize-none" placeholder="Describe the defect, when it happens, which decks, etc." data-testid="input-defect-desc" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Anilox BCM</label>
                <Input type="number" step="0.1" value={form.anilox_bcm}
                       onChange={(e) => setForm({ ...form, anilox_bcm: e.target.value })}
                       className="rounded-sm" placeholder="e.g. 3.2" data-testid="input-bcm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Anilox LPI</label>
                <Input type="number" value={form.anilox_lpi}
                       onChange={(e) => setForm({ ...form, anilox_lpi: e.target.value })}
                       className="rounded-sm" placeholder="e.g. 1200" data-testid="input-lpi" />
              </div>
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-1.5">Pantone / target color</label>
              <Input value={form.pantone_color} onChange={(e) => setForm({ ...form, pantone_color: e.target.value })}
                     className="rounded-sm" placeholder="e.g. PMS 186 C" data-testid="input-pantone" />
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-1.5">Additional context</label>
              <Textarea rows={2} value={form.additional_context}
                        onChange={(e) => setForm({ ...form, additional_context: e.target.value })}
                        className="rounded-sm resize-none" placeholder="Ambient conditions, prior fixes, urgency, etc." data-testid="input-context" />
            </div>

            <Button
              onClick={run}
              disabled={running}
              className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white font-mono-plex uppercase tracking-widest text-sm"
              data-testid="copilot-run-btn"
            >
              {running ? (
                <>
                  <span className="relative flex h-2 w-2 mr-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                  </span>
                  Analyzing…
                </>
              ) : (<><Sparkles className="w-4 h-4 mr-2" /> Analyze case <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>

            <button onClick={reset} className="w-full text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground py-1" data-testid="copilot-reset-btn">
              <RotateCcw className="w-3 h-3 inline mr-1" /> Reset form
            </button>
          </div>
        </div>

        {/* OUTPUT */}
        <div className="lg:col-span-3">
          <div className="border border-border bg-card rounded-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="overline text-primary">◉ ANALYSIS</div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={copy} disabled={!output} className="h-8 rounded-sm text-xs" data-testid="copilot-copy-btn">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                </Button>
                <Button size="sm" variant="ghost" onClick={download} disabled={!output} className="h-8 rounded-sm text-xs" data-testid="copilot-download-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> .md
                </Button>
                <Button size="sm" onClick={save} disabled={!output || saving} className="h-8 rounded-sm bg-primary hover:bg-primary/90 text-xs" data-testid="copilot-save-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
            <div
              ref={outRef}
              className="p-6 lg:p-8 text-[14px] leading-relaxed max-h-[calc(100vh-14rem)] overflow-y-auto whitespace-pre-wrap font-sans"
              data-testid="copilot-output"
            >
              {output ? (
                <>
                  {output}
                  {running && <span className="inline-block w-2 h-4 bg-accent animate-pulse ml-0.5 align-middle" />}
                </>
              ) : (
                <div className="text-muted-foreground py-16 text-center">
                  <FileText className="w-8 h-8 mx-auto mb-3 opacity-40" />
                  <div className="font-display text-lg text-foreground">Awaiting case inputs</div>
                  <p className="text-sm mt-2 max-w-md mx-auto">
                    Fill in the customer, press, substrate, and defect on the left.
                    The Copilot returns a full 9-section technical analysis in ~30 seconds.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
